/**
 * useXoeChatSession — the headless Xoe chat brain.
 * *** THIS FILE MUST STAY `.ts`, NOT `.tsx`. *** It is the mechanical check on the
 * boundary: if it needs JSX, something presentational has leaked in. The delete
 * confirmation is the worked example — the hook exposes `deleteActiveConversation()`,
 * each skin owns its own dialog.
 *
 * ── Why a hook and not a Context ──────────────────────────────────────────────
 * A Context would work — `App.tsx` mounts `XoeFocusProvider` around both `<Routes>` (the
 * page) and `<XoeWidget />`, so a genuine common ancestor exists. It is still wrong:
 * a provider would hold the entire session — both React Query observers, the abort
 * controller, the turn state — alive on every route for every practitioner who never
 * opens Xoe
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
	useXoeConversations,
	useXoeConversation,
	useCreateXoeConversation,
	useStreamXoeMessage,
	useUpdateXoeConversation,
	useDeleteXoeConversation,
} from './useXoe';
import { streamXoeMessage, XoeStreamError } from '../../api/xoe/XoeStreamApi';
import { useXoeFocus } from '../../context/XoeFocusContext';
import { xoeTurnContext } from '../../utils/xoe/pageContext';
import type { XoeConversation } from '../../types/xoe/XoeTypes';
import type { XoeTurnProgress } from '../../types/xoe/XoeStreamTypes';
import type { StagedPatient, XoeChatSession, UseXoeChatSessionOptions } from '../../types/xoe/XoeChatTypes';
import { isXoeAgentEnabled } from '../../api/xoe/xoeBaseUrl';
import type { UserUuid } from '../../types/identity';

// ── The conversation the two surfaces share ──────────────────────────────

/**
 * The uuid the chat was last showing, in whichever host rendered it.
 */
let lastActiveConversationUuid: string | null = null;

/** The uuid the chat was last showing this session, or null. See above. */
export const getLastActiveXoeConversation = (): string | null => lastActiveConversationUuid;

/**
 * Records the conversation the calling surface is now showing.
 
 */
export const publishActiveConversation = (uuid: string | null): void => {
	lastActiveConversationUuid = uuid;
};

/**
 * Test-only reset.
 *
 * @internal
 *
 * ⚠️ Jest gives each test FILE a fresh module registry, but not each `it()`. Without
 * this the memo leaks between cases within a file, so a suite that asserts on it is
 * silently order-dependent — it passes today only because the first case happens to
 * set the value the later ones expect. Call it in `beforeEach`.
 */
export const __resetLastActiveXoeConversation = (): void => {
	lastActiveConversationUuid = null;
};

// ── Concurrency guard ─────────────────────────────────────────────────────────

let liveSessionCount = 0;

/**
 * Warns if two chat sessions are ever live at once.
 */
const useSessionConcurrencyGuard = (): void => {
	useEffect(() => {
		liveSessionCount += 1;
		if (liveSessionCount > 1) {
			// eslint-disable-next-line no-console
			console.error(
				'[useXoeChatSession] Two Xoe chat sessions are mounted at once. ' +
					'They will fight over the in-flight turn and the cross-surface conversation memo. ' +
					'Check XoeWidget stands down on the full-page route.',
			);
		}
		return () => {
			liveSessionCount -= 1;
		};
	}, []);
};

/** Test-only reset for the guard above. @internal */
export const __resetXoeSessionCount = (): void => {
	liveSessionCount = 0;
};


// ── The hook ──────────────────────────────────────────────────────────────────

export function useXoeChatSession({
	active,
	initialUuid,
	initialUuidToken,
	initialSeedText,
	defaultExpertId,
}: UseXoeChatSessionOptions): XoeChatSession {
	useSessionConcurrencyGuard();

	// ── Active conversation ───────────────────────────────────────────────────
	const [activeUuid, setActiveUuid] = useState<string | null>(null);

	// ORDER 1 — Publish the current conversation so the OTHER surface can carry on in it.
	useEffect(() => {
		publishActiveConversation(activeUuid);
	}, [activeUuid]);

	// ── Pending turn tracking ─────────────────────────────────────────────────
	const [turnProgress, setTurnProgress] = useState<XoeTurnProgress | null>(null);
	const abortRef = useRef<AbortController | null>(null);
	const turnIdRef = useRef(0);
	const turnInFlightRef = useRef(false);

	// ── Optimistic user message ───────────────────────────────────────────────
	const [pendingText, setPendingText] = useState<string | null>(null);

	// ── Memory proposals awaiting review
	const [pendingMemoryProposals, setPendingMemoryProposals] = useState(0);

	// ── Failed send attempt ─────────────────────────────
	const [streamError, setStreamError] = useState<{ message: string; text: string } | null>(null);

	// ── Queries and mutations ─────────────────────────────────────────────────
	// `active` gates the list so a host that is not live (the floating widget on a signed-out page) fires no request.
	const { data: conversations } = useXoeConversations(undefined, undefined, active);
	const { applyCompletedTurn, applyErrorTurn } = useStreamXoeMessage();
	const { mutateAsync: createConversationAsync } = useCreateXoeConversation();
	const { mutateAsync: updateConversationAsync } = useUpdateXoeConversation();
	const { mutateAsync: deleteConversationAsync } = useDeleteXoeConversation();


	// ── the focus a TURN reads ───────────────────────────────────────────
	const focus = useXoeFocus();
	const focusRef = useRef(focus);

	useEffect(() => {
		focusRef.current = focus;
	}, [focus]);

	// The patient the NEXT created conversation will be bound to. See `StagedPatient`.
	const [stagedPatient, setStagedPatient] = useState<StagedPatient | null>(null);
	const stageWhenNamedRef = useRef<UserUuid | null>(null);

	/**
	 * Stage the CURRENTLY focused patient for the next created conversation.
	 */
	const stageFocusedPatient = useCallback(() => {
		const id = focus.patientId ?? null;
		const label = focus.patientLabel;

		if (id !== null && label) {
			stageWhenNamedRef.current = null;
			setStagedPatient({ id, label });
			return;
		}
		stageWhenNamedRef.current = id;
		setStagedPatient(null);
	}, [focus.patientId, focus.patientLabel]);

	/** Drop any staged patient AND any pending deferral.  */
	const clearStagedPatient = useCallback(() => {
		stageWhenNamedRef.current = null;
		setStagedPatient(null);
	}, []);

	const appliedFocusPatientRef = useRef<UserUuid | null>(null);

	const { data: activeDetail } = useXoeConversation(activeUuid ?? '');

	// The bound patient's display name, surfaced once a conversation is bound to a patient
	const boundPatientName =
		activeDetail?.conversation?.patient_id != null
			? activeDetail.conversation.title
			: (stagedPatient?.label ?? null);

	// ── Expert selection ──────────────────────────────────────────────────────
	// Holds the expert choice before a conversation is created (null = Auto).
	const [pendingExpertId, setPendingExpertId] = useState<string | null>(null);
	const effectivePending = turnProgress !== null;

	// ── Turn lifecycle ──────────────────────────────────────
	//
	// A turn can be started from TWO places — the composer (via `send`, which may create a
	// conversation first) and the Discuss-with-Xoe seed handoff below (which never creates
	// one) — without duplicating the pending/failure/completion bookkeeping. Declared here,
	// ABOVE the seed effect that needs `runTurn`.

	/**
	 * Consume one streamed turn, folding each frame into `turnProgress`.
	 */
	const runStreamedTurn = useCallback(
		async (uuid: string, text: string) => {
			const controller = new AbortController();
			abortRef.current = controller;
			const context = xoeTurnContext(focusRef.current);

			await streamXoeMessage({
				uuid,
				text,
				context,
				signal: controller.signal,
				onEvent: (event) => {
					switch (event.event) {
						case 'tool.requested':
							setTurnProgress((prev) =>
								prev
									? {
											...prev,
											// APPEND, so arrival order is the render order.
											entries: [
												...prev.entries,
												{
													kind: 'tool',
													activity: {
														toolUseId: event.tool_use_id,
														tool: event.tool,
														// Verbatim from the wire — no client-side label table.
														label: event.label,
														state: 'running',
													},
												},
											],
										}
									: prev,
							);
							break;

						case 'tool.completed':
							setTurnProgress((prev) =>
								prev
									? {
											...prev,
											// Updates IN PLACE — `tool.completed` resolves an entry that is
											// already in the timeline and must not move it, or a tool that
											// finishes late would jump ahead of reasoning that arrived while
											// it was running.
											entries: prev.entries.map((entry) =>
												entry.kind === 'tool' &&
												entry.activity.toolUseId === event.tool_use_id
													? {
															...entry,
															activity: {
																...entry.activity,
																state: event.denied
																	? 'denied'
																	: event.ok
																		? 'done'
																		: 'failed',
																durationMs: event.duration_ms,
															},
														}
													: entry,
											),
										}
									: prev,
							);
							break;

						// P7.1. One completed thinking block, already scrubbed server-side.
						// Appended to the SAME array as tool activity, which is what makes
						// the interleaving real rather than reconstructed.
						case 'reasoning.block':
							setTurnProgress((prev) =>
								prev
									? {
											...prev,
											entries: [
												...prev.entries,
												{
													kind: 'reasoning',
													entry: { index: event.index, text: event.text },
												},
											],
										}
									: prev,
							);
							break;

						case 'turn.capped':
							setTurnProgress((prev) => (prev ? { ...prev, capped: true } : prev));
							break;

						case 'turn.completed':
							applyCompletedTurn(uuid, text, event);
							// Absent on a pre-S5 server; practitioner-wide, so replaced, never accumulated.
							setPendingMemoryProposals(event.pending_memory_proposals ?? 0);
							break;

						case 'turn.error':
							applyErrorTurn(uuid, text, event);
							break;

						// `turn.started` needs no state change — the progress object already
						// exists from the click, which is earlier and therefore more responsive.
						default:
							break;
					}
				},
			});
		},
		[applyCompletedTurn, applyErrorTurn],
	);

	/** Show the pending feedback (activity block + composer lock) and the optimistic user
	 *  bubble for a turn about to run. Always called at the START of a turn, by `runTurn`. */
	const beginTurn = useCallback((text: string, startedAt: number) => {
		// A new attempt supersedes any previous transport failure.
		setStreamError(null);
		// Written alongside the state, not derived from it — see `turnInFlightRef`.
		turnInFlightRef.current = true;
		setTurnProgress({ startedAt, entries: [], capped: false });
		setPendingText(text);
	}, []);

	/** Clear the pending feedback once a turn is done. Callers never call this directly —
	 *  `runTurn`'s turnId guard decides WHETHER a settling turn is still the current one
	 *  before calling it. */
	const endTurn = useCallback(() => {
		abortRef.current = null;
		turnInFlightRef.current = false;
		setTurnProgress(null);
		setPendingText(null);
	}, []);

	const acknowledgeMemoryProposals = useCallback(() => setPendingMemoryProposals(0), []);

	/**
	 * Surface a failed SEND ATTEMPT (the turn never started) into `sendFailure`. See
	 * `XoeSendFailure` for why this is local state and not the query cache.
	 */
	const reportTurnFailure = useCallback((err: unknown, text: string) => {
		const isStreamError = err instanceof XoeStreamError;
		setStreamError({
			message: isStreamError
				? err.userMessage
				: 'Xoe could not be reached. Please try again.',
			text,
		});
		// Status/shape only — never the error object, which can carry a body.
		// eslint-disable-next-line no-console
		console.error(
			'[useXoeChatSession] send failed',
			isStreamError ? { status: err.status } : { status: null },
		);
	}, []);

	/**
	 * Run one turn against an EXISTING conversation `uuid`
	 */
	const runTurn = useCallback(
		async (uuid: string, text: string, startedAt: number = Date.now()) => {

			if (!isXoeAgentEnabled()) {
				reportTurnFailure(new Error('xoe-agent host is not configured'), text);
				return;
			}

			abortRef.current?.abort();
			const turnId = ++turnIdRef.current;

			beginTurn(text, startedAt);

			try {
				await runStreamedTurn(uuid, text);
			} catch (err) {
				// Only the CURRENT turn may report a failure
				if (turnId === turnIdRef.current) {
					reportTurnFailure(err, text);
				}
			} finally {
				// Only the CURRENT turn may clear pending state — see the turnId guard
				// discussion above.
				if (turnId === turnIdRef.current) {
					endTurn();
				}
			}
		},
		[runStreamedTurn, beginTurn, reportTurnFailure, endTurn],
	);

	// ── Auto-select most recent conversation on open ──────────────────────────
	const hasAutoSelectedRef = useRef(false);

	// ORDER 3
	useEffect(() => {
		if (!active) {
			hasAutoSelectedRef.current = false;
		}
	}, [active]);

	// ORDER 4
	useEffect(() => {
		if (
			active &&
			!hasAutoSelectedRef.current &&
			!activeUuid &&
			conversations &&
			conversations.length > 0
		) {
			hasAutoSelectedRef.current = true;
			setActiveUuid(conversations[0].uuid);
		}
	}, [active, conversations, activeUuid]);

	// ── Discuss-with-Xoe handoff: focus the requested conversation ────────
	useEffect(() => {
		if (initialUuid) {
			hasAutoSelectedRef.current = true;
			appliedFocusPatientRef.current = focus.patientId ?? null;
			clearStagedPatient();
			setActiveUuid(initialUuid);
			setTurnProgress(null);
			setPendingText(null);
			setPendingExpertId(null);
			setStreamError(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialUuid, initialUuidToken]);

	// ── Discuss-with-Xoe seed handoff: run the seed text as a turn ──────
	const lastSeedRunRef = useRef<string | null>(null);

	useEffect(() => {
		if (!initialUuid || !initialSeedText) return;

		const seedKey = `${initialUuid}\0${initialSeedText}`;
		if (lastSeedRunRef.current === seedKey) return;
		lastSeedRunRef.current = seedKey;

		// `initialUuid`, not `activeUuid` — see the "detail A" note on `runTurn` above.
		void runTurn(initialUuid, initialSeedText);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialUuid, initialUuidToken, initialSeedText]);

	// ── Handlers ──────────────────────────────────────────────────────────────

	const handleSelectConversation = useCallback((conv: XoeConversation) => {
		appliedFocusPatientRef.current = focus.patientId ?? null;
		clearStagedPatient();
		setActiveUuid(conv.uuid);
		setTurnProgress(null);
		setStreamError(null);
		setPendingText(null);
		setPendingExpertId(null);
	}, [focus.patientId, clearStagedPatient]);

	/**
	 * Start a fresh, not-yet-created conversation.
	 */
	const startNewConversationInternal = useCallback(
		(general: boolean) => {
			// Mark auto-select as already consumed so clearing activeUuid below does not
			// re-trigger the "land on most recent" effect (Issue: New snaps back).
			hasAutoSelectedRef.current = true;
			appliedFocusPatientRef.current = focus.patientId ?? null;
			if (general) {
				clearStagedPatient();
			} else {
				stageFocusedPatient();
			}
			setActiveUuid(null);
			setTurnProgress(null);
			setStreamError(null);
			setPendingText(null);
			setPendingExpertId(null);
		},
		[focus.patientId, stageFocusedPatient, clearStagedPatient],
	);

	const startNewConversation = useCallback(
		() => startNewConversationInternal(false),
		[startNewConversationInternal],
	);

	const startNewGeneralConversation = useCallback(
		() => startNewConversationInternal(true),
		[startNewConversationInternal],
	);

	/**
	 * Delete a conversation by uuid. Permanent, and cascades to its messages — the
	 * CONFIRMATION IS THE SKIN'S JOB.
	 */
	const deleteConversationByUuid = useCallback(
		async (uuid: string) => {
			const wasActive = uuid === activeUuid;
			await deleteConversationAsync({ uuid });
			if (!wasActive) return;

			hasAutoSelectedRef.current = true;
			setActiveUuid(null);
			stageFocusedPatient();
			setTurnProgress(null);
			setPendingText(null);
			setPendingExpertId(null);
		},
		[activeUuid, deleteConversationAsync, stageFocusedPatient],
	);

	const deleteActiveConversation = useCallback(async () => {
		if (!activeUuid) return;
		await deleteConversationByUuid(activeUuid);
	}, [activeUuid, deleteConversationByUuid]);

	/**
	 * Rename a conversation. See `XoeChatSession.renameConversation` — callers must refuse
	 * this for a patient-bound conversation.
	 */
	const renameConversation = useCallback(
		async (uuid: string, title: string) => {
			await updateConversationAsync({ uuid, title });
		},
		[updateConversationAsync],
	);

	// Before a conversation exists the choice rides the create payload; after, it is PATCHed (null = Auto).
	const selectExpert = useCallback(
		async (id: string | null) => {
			if (!activeUuid) {
				setPendingExpertId(id);
				return;
			}
			await updateConversationAsync({ uuid: activeUuid, expert_id: id });
		},
		[activeUuid, updateConversationAsync],
	);

	/** Archive or un-archive a conversation. Archived rows are filtered server-side, so
	 *  the row leaves (or returns to) the list on the mutation's own invalidation. */
	const archiveConversation = useCallback(
		async (uuid: string, archived: boolean) => {
			await updateConversationAsync({
				uuid,
				archived_at: archived ? new Date().toISOString() : null,
			});
		},
		[updateConversationAsync],
	);

	/** Cancel the in-flight streamed turn. Aborting closes the stream, which cancels the
	 *  loop server-side, so it genuinely stops burning tokens rather than hiding the
	 *  result. Nothing is persisted for a cancelled turn (P6.1's to resolve). */
	const handleCancelTurn = useCallback(() => {
		abortRef.current?.abort();
	}, []);

	const sendInFlightRef = useRef(false);

	/**
	 * Composer-driven send. Creates a conversation first when none is active yet (titled
	 * from the QUESTION text — unlike the Discuss-with-Xoe handoff, which is titled
	 * from the record by the calling page instead), then hands off to `runTurn` for the
	 * transport-agnostic part.
	 */
	const handleSend = useCallback(
		async (text: string) => {
			if (!text.trim() || turnProgress !== null || sendInFlightRef.current) return;
			sendInFlightRef.current = true;

			try {
				let targetUuid = activeUuid;

				if (!targetUuid) {
					try {
						const newConv = await createConversationAsync({
							...(stagedPatient
								? { patient_id: stagedPatient.id, title: stagedPatient.label }
								: { title: text.slice(0, 60) }),
							...((pendingExpertId ?? defaultExpertId) != null
								? { expert_id: (pendingExpertId ?? defaultExpertId) as string }
								: {}),
						});
						targetUuid = newConv.uuid;
						clearStagedPatient();
						setActiveUuid(targetUuid);
						setPendingExpertId(null);
					} catch (err) {
						const status = (err as { response?: { status?: unknown } } | undefined)?.response?.status;
						if (stagedPatient && (status === 404 || status === 503)) {
							clearStagedPatient();
							setStreamError({
								message: (status === 404
									? 'Xoe could not link this conversation to that patient record, so nothing was sent.'
									: 'Xoe could not reach the patient record service, so nothing was sent. Please try again.'
								), text
							});

							console.error('[useXoeChatSession] patient bind refused', { status });
							return;
						}
						// Creation itself failed — no turn ever started, so surface it the same
						// way a transport failure is surfaced and stop.
						reportTurnFailure(err, text);
						return;
					}
				}

				await runTurn(targetUuid, text);
			} finally {
				sendInFlightRef.current = false;
			}
		},
		[
			activeUuid,
			turnProgress,
			createConversationAsync,
			pendingExpertId,
			defaultExpertId,
			stagedPatient,
			runTurn,
			reportTurnFailure,
			clearStagedPatient,
		],
	);

	/** Re-send the message that failed. Declared after `handleSend` so the closure is
	 *  the current one; `handleSend` clears the notice itself on entry. */
	const handleRetryStreamError = useCallback(() => {
		if (!streamError) return;
		void handleSend(streamError.text);
	}, [streamError, handleSend]);

	const handleDismissStreamError = useCallback(() => {
		setStreamError(null);
	}, []);

	// ── Follow the practitioner's focus
	useEffect(() => {
		const focusPatientId = focus.patientId ?? null;

		// A CHANGE of record takes control
		if (appliedFocusPatientRef.current === focusPatientId) return;

		// *** NEVER STEAL A TURN THAT IS ALREADY RUNNING.
		if (turnInFlightRef.current) return;

		// The list has not arrived yet. Leave the focus UNCONSUMED so this re-runs once it
		// has, rather than staging a patient whose conversation already exists.
		if (!conversations) return;

		appliedFocusPatientRef.current = focusPatientId;
		// Landing here counts as "the session has been placed", so the auto-select-on-open
		// effect must not snap back over it — the same reason `startNewConversation` sets this.
		hasAutoSelectedRef.current = true;

		// One predicate covers both directions: a focused record finds that patient's most
		// recent conversation, a cleared focus (`null`) finds the most recent GENERAL one.
		const target = conversations.find((c) => c.patient_id === focusPatientId) ?? null;

		if (target) {
			clearStagedPatient();
			setActiveUuid(target.uuid);
		} else {
			// *** STAGED, NEVER CREATED. *** 
			stageFocusedPatient();
			setActiveUuid(null);
		}

		// The same reset a manual switch does — an optimistic bubble or a failure notice
		// belongs to the conversation being left behind, not to the one arriving.
		setPendingText(null);
		setStreamError(null);
		setPendingExpertId(null);
		// `turnProgress` is a RE-RUN TRIGGER, not an input: the gate above reads the ref.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [focus.patientId, focus.patientLabel, conversations, turnProgress]);

	// ORDER 8 — the label can arrive, or change, AFTER the id it belongs to: `PatientModal`
	// derives it from a record it is still loading (`loadAllPatientData`) and that the
	// practitioner can then edit. 
	useEffect(() => {
		const label = focus.patientLabel;
		// Same truthiness rule as `stageFocusedPatient`: an empty display name is not a name.
		if (!label) return;

		if (stageWhenNamedRef.current !== null && stageWhenNamedRef.current === focus.patientId) {
			const id = stageWhenNamedRef.current;
			stageWhenNamedRef.current = null;
			setStagedPatient({ id, label });
			return;
		}

		setStagedPatient((prev) =>
			prev && prev.id === focus.patientId && prev.label !== label
				? { ...prev, label }
				: prev,
		);
	}, [focus.patientId, focus.patientLabel]);

	// ── The session ───────────────────────────────────────────────────────────
	return {
		activeUuid,
		activeDetail,
		boundPatientName,
		focusPatientId: focus.patientId ?? null,
		canDelete: !!activeUuid,
		// A loaded conversation's own pin wins, including Auto (null) — else a stale pending choice would reappear.
		expertId: activeDetail
			? (activeDetail.conversation.expert_id ?? null)
			: (pendingExpertId ?? defaultExpertId ?? null),
		turn: {
			progress: turnProgress,
			isPending: effectivePending,
			pendingText,
			cancel: handleCancelTurn,
		},
		sendFailure: streamError
			? {
					message: streamError.message,
					retry: handleRetryStreamError,
					dismiss: handleDismissStreamError,
				}
			: null,
		pendingMemoryProposals,
		send: handleSend,
		selectConversation: handleSelectConversation,
		startNewConversation,
		startNewGeneralConversation,
		deleteActiveConversation,
		deleteConversation: deleteConversationByUuid,
		renameConversation,
		archiveConversation,
		selectExpert,
		acknowledgeMemoryProposals,
	};
}

export default useXoeChatSession;
